import KubernetesAppName from '~/constants/common/KubernetesAppName';
import dealershipDomains from '~/constants/dealership/dealershipDomains';
import { vexStockImages } from '~/constants/environment';
import COOKIES from './cookies';

if (
  process.env.CVNA_APP_ANALYTICS_BASE_URL == null ||
  process.env.CVNA_APP_APIMPUB_BASE_URL == null ||
  process.env.CVNA_APIK_BASE_URL == null
) {
  throw new Error('Base URL environment variable(s) missing.');
}

export const ANALYTICS_BASE_URL = process.env.CVNA_APP_ANALYTICS_BASE_URL;
export const APIMPUB_BASE_URL = process.env.CVNA_APP_APIMPUB_BASE_URL;
export const APIK_BASE_URL = process.env.CVNA_APIK_BASE_URL;
// Sebastian Questions API
export const DESCARTES_BASE_URL = process.env.CVNA_DESCARTES_BASE_URL;

type MerchUiBaseUrlOptions = {
  currentPageUrl: string;
};

export function getMerchUiBaseUrl(
  usesAuthentication: boolean,
  { currentPageUrl }: MerchUiBaseUrlOptions
): string {
  const isLocal = Boolean(process.env.IS_LOCAL);
  const isLocalAndProxied =
    isLocal &&
    ['carvana.com']
      .concat(Object.keys(dealershipDomains))
      .some(domain => currentPageUrl.includes(domain));

  if (isLocalAndProxied) {
    return `https://localhost.carvanatech.com/merch/${KubernetesAppName.MerchUI}`;
  }

  // Phantom Tokens cannot be used directly, they must be proxied through Kong, so we need to use apik
  if (usesAuthentication || !isLocal) {
    return `${APIK_BASE_URL}/merch/${KubernetesAppName.MerchUI}`;
  }

  const url = new URL(currentPageUrl);

  return `${url.protocol}//${url.host}`; // e.g. http://localhost:3000
}

// API URLs

export const DISCOUNTS = `${APIK_BASE_URL}/core/discount/api/v1/discount`;

export const GEOLOCATION = `${ANALYTICS_BASE_URL}/edge/geolocation/api/v1/geolocation`;

export const GET_VEHICLE_RECOMMENDATIONS = `${APIK_BASE_URL}/merch/search/api/v1/recommendations`;

export const GET_PERSONALIZED_RECOMMENDATIONS = `${APIK_BASE_URL}/merch/search/api/v1/recommendations/personalized-recs`;

export const CONTACTS = `${APIK_BASE_URL}/core/contacts/api/v1/details/`;

export const GET_USER_PROFILE = `${APIK_BASE_URL}/core/user-profile-api/api/graph`;

export const GET_FAVORITES = `${APIK_BASE_URL}/merch/rcbapi/api/v1/favorites`;

export const ADD_FAVORITES = `${APIK_BASE_URL}/merch/rcbapi/api/v1/favorites/add`;

export const REMOVE_FAVORITE = `${APIK_BASE_URL}/merch/rcbapi/api/v1/favorites/remove`;

export const GET_USER_PURCHASING = `${APIK_BASE_URL}/merch/rcbapi/api/v1/purchase`;

export const USER_VEHICLE_AVAILABILITY = `${APIK_BASE_URL}/merch/vehicledetails/api/v1/availability`;

export const LOCATION_INFORMATION_FROM_ZIP = `${APIK_BASE_URL}/merch/carvanamerchcontextapi/api/v2/delivery/location`;

export const GET_PREORDER_INTENT = `${APIK_BASE_URL}/merch/vehicledetails/api/v1/purchase/intent`;

export const START_PREORDER = `${APIK_BASE_URL}/merch/vehicledetails/api/v1/purchase/start`;

export const CANCEL_PREORDER = `${APIK_BASE_URL}/merch/vehicledetails/api/v1/purchase/cancel`;

export const CANCEL_PURCHASE = `${APIK_BASE_URL}/oec/purchase/api/v1/purchase/cancelpurchase`;

export const CUSTOMER_JOURNEY_SERVICE = `${APIK_BASE_URL}/edge/customerjourney/graphql`;

export const GET_INSPECTION_LIST = `${APIK_BASE_URL}/merch/vehicledetails/api/v1/inspection/report`;

export const GET_GAP_ANCILLARY = `${APIMPUB_BASE_URL}/ancillary-products-api/api/v2/GapWaiver/availability`;

export const SUBSCRIBE_TO_VEHICLE_AVAILABILITY = `${APIK_BASE_URL}/merch/subscriptionnotifications/api/v1/vehicleavailability`;

export const RECALL_STATUS = `${APIK_BASE_URL}/merch/vehicledetails/api/v1/recall`;

export const GET_OWNER_REVIEWS = `${APIK_BASE_URL}/merch/reviews/api/review/sv`;

export const BUYERS_GUIDE = `${APIK_BASE_URL}/oec/contracts/api/v1/vdp`;

export const INVENTORY = `${APIK_BASE_URL}/merch/search/api/v2/search`;
export const DEALERSHIP_INVENTORY = `${APIK_BASE_URL}/merch/search/api/v2/search/dealership`;
export const FACETS = `${APIK_BASE_URL}/merch/search/api/v2/search/facets`;
export const DEALERSHIP_FACETS = `${APIK_BASE_URL}/merch/search/api/v2/search/facets/dealership`;

export const DELIVERY_CONTEXT = `${APIK_BASE_URL}/merch/carvanamerchcontextapi/api/v3/delivery`;

export const GET_DELIVERY_PICKUP = `${APIK_BASE_URL}/merch/buffering/api/v1/soonestavailabilitybulk`;

export const DISTANCE_CONTEXT = `${APIK_BASE_URL}/merch/carvanamerchcontextapi/api/v3/distance`;

export const getCreditStatus = (opts: MerchUiBaseUrlOptions) =>
  `${getMerchUiBaseUrl(true, opts)}/api/v1/credit-status`;

export const getToggleCoApplicant = (opts: MerchUiBaseUrlOptions) =>
  `${getMerchUiBaseUrl(true, opts)}/api/v1/toggle-coapplicant`;

export const ANONYMOUS_SAVED_SEARCH = `${APIK_BASE_URL}/merch/savedsearch/api/v2/anonymous`;

export const RCB_SAVED_SEARCH = `${APIK_BASE_URL}/merch/rcbapi/api/v1/savedsearch`;

export const GET_SUGGESTIONS_V4 = `${APIK_BASE_URL}/merch/search/api/v4/suggest`;

export const OPEN_PERSONALIZED_RECS = `${APIK_BASE_URL}/merch/search/api/v1/recommendations/open-personalized-recs`;

export const LOWER_DOWN_RECS = `${APIK_BASE_URL}/merch/search/api/v1/recommendations/cash-due-recirc-recs`;

export const getServiceContracts = (opts: MerchUiBaseUrlOptions) =>
  `${getMerchUiBaseUrl(false, opts)}/api/v1/servicecontracts`;

export const getGetRecentSearches = (opts: MerchUiBaseUrlOptions) =>
  `${getMerchUiBaseUrl(true, opts)}/api/v1/recentsearches`;

export const getAnonymousRecentVehicles = (opts: MerchUiBaseUrlOptions) =>
  `${getMerchUiBaseUrl(true, opts)}/api/v2/recentvehicles`;

export const getAuthenticatedRecentVehicles = `${APIK_BASE_URL}/merch/rcbapi/api/v1/rcb/userviews`;

export const getBlueSteel = (opts: MerchUiBaseUrlOptions) =>
  `${getMerchUiBaseUrl(false, opts)}/api/v1/bluesteel`;

export const getTradesV2 = (opts: MerchUiBaseUrlOptions) =>
  `${getMerchUiBaseUrl(true, opts)}/api/v2/trades`;

export const getFinancingTerms = (
  applyTradeIn: boolean,
  opts: MerchUiBaseUrlOptions
) =>
  `${getMerchUiBaseUrl(true, opts)}/api/refresh-terms?${
    COOKIES.tradeInApplied
  }=${applyTradeIn}`;

export const toggleFinancingCoApplicant = (
  applyTradeIn: boolean,
  opts: MerchUiBaseUrlOptions
) =>
  `${getMerchUiBaseUrl(true, opts)}/api/toggle-coapplicant?${
    COOKIES.tradeInApplied
  }=${applyTradeIn}`;

export const PRICING = `${APIK_BASE_URL}/merch/search/api/v2/pricing`;

export const VEHICLE_PRICING = `${APIK_BASE_URL}/merch/search/api/v2/pricing/vehicle`;

export const WHY_BUY = `${APIK_BASE_URL}/merch/vehicledetails/api/v1/whybuy`;

export const VERIFY_EMAIL = `${APIK_BASE_URL}/core/contacts/api/v2/details/email/verify`;

export const FORCE_BUCKET = `${ANALYTICS_BASE_URL}/edge/split-test/api/v3/identifier/chosen-bucket`;

export const SEO_CONTENT_CLIENT = `${APIK_BASE_URL}/traffic/content`;

export const SEO_REVIEWS_CLIENT = `${APIK_BASE_URL}/traffic/reviews/v4`;

export const SEO_LINKS_CLIENT = `${APIK_BASE_URL}/traffic/links/v3/kill/byurl`;

export const SUGGESTED_FILTERS = `${APIK_BASE_URL}/merch/search/api/v1/suggest/filters`;

export const SEBS_API = `${DESCARTES_BASE_URL}/vehicle-questions/`;

export const getGeocode = (opts: MerchUiBaseUrlOptions) =>
  `${getMerchUiBaseUrl(false, opts)}/api/v1/geocode`;

export const GENERIC_VEHICLE_IMAGE = `${vexStockImages}/generic_vehicle_desktop_1920x1080.png`;

export const UPDATE_PASSWORD_WITH_PARTNER_ACTIVATION_TOKEN = `${APIK_BASE_URL}/ca/accountactivationapi/api/v1/accountactivation/updatepassword`;

export const SEND_ACTIVATION_NOTIFICATION = `${APIK_BASE_URL}/ca/accountactivationapi/api/v2/accountactivation/notification`;

export const NEW_CAR_CONFIGURATION = `${APIK_BASE_URL}/merch/search/api/v2/newcarconfiguration`;
export const NEW_CAR_CUSTOMIZE = `${APIK_BASE_URL}/merch/search/api/v2/newcarconfiguration/customize`;
export const NEW_CAR_DELIVERY = `${APIK_BASE_URL}/merch/search/api/v1/Delivery`;
